import React, { useState, useEffect, useMemo } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { v4 as uuid } from "uuid";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import actions from "../Store/actions";
import {
    useAssetTypeIdentifiers,
    useAssetTypeMakes,
    useAssetTypesForAuditOrganisation,
    useAssetTypeValidationRules,
    useDistinctAssetTypesForAudit,
    useDistinctMakesForAudit,
    useIsScrollingPresent,
    useAudit,
    useToaster,
} from "../common/hooks";
import NativeCamera from "../common/components/NativeCamera";
import useMakes from "./useMakes";
import { useTranslation } from "react-i18next";
import { BrowserCamera, PhotoList, ActionBar } from "../common/components";
import CameraLocationError from "../common/components/CameraLocationError";
import {
    Box,
    TextField,
    Button,
    ButtonGroup,
    FormLabel,
    FormControl,
    MenuItem,
    Grid,
} from "@mui/material/";
import Autocomplete from "@mui/material/Autocomplete";
import CheckIcon from "@mui/icons-material/Check";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import hdlIdentifierValidation from "./altAssetUtils";
import styles from "./styles";
import {
    GroupHeader,
    StyledPopper,
    GroupItems,
} from "./styledAltAssetComponents";
import config from "../config";
import ImageTypeModal from "./ImageTypeModal";
import LoadingButton from "@mui/lab/LoadingButton";
import { logToSentry } from "../config/Sentry";

const altAssetData = {
    audit: null,
    auditImages: [],
    identifier1: "",
    identifier2: "",
    identifier3: "",
    identifier4: "",
    make: 0,
    note: { content: "", createdDate: null },
    type: 0,
};
const { searchByValue, searchByIdentifier } = config.altAsset;
/**
 * Alt-asset Component
 */

const identifier1MinLength = 7;

export default () => {
    const classes = makeStyles(styles)();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { auditId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const toast = useToaster();

    const {
        typesList,
        audits,
        auditAssets,
        appSettings,
        app,
        auth,
        auditUpload,
    } = useSelector((state) => state);
    const { imageSyncEnabled, netConnected } = app;
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);

    const [altAsset, setAltAsset] = useState(altAssetData);

    const [searchedByIdentifier, setSearchedByIdentifier] = useState({
        value: searchByValue,
        name: searchByIdentifier,
    });
    // TODO: temporary to support Native camera
    const [altImageType, setAltImageType] = useState(0);
    const [imageTypeModalOpen, setImageTypeModalOpen] = useState(false);

    const isScrollingPresent = useIsScrollingPresent("alt-asset-form");
    const assetTypes = useAssetTypesForAuditOrganisation();

    const distinctAssetTypes = useDistinctAssetTypesForAudit();

    const audit = useAudit(auditId);
    const identifiers = useAssetTypeIdentifiers(altAsset.type);
    const rules = useAssetTypeValidationRules(altAsset.type);

    const distinctMakes = useDistinctMakesForAudit(auditId);
    const assetTypeMakes = useAssetTypeMakes(altAsset.type);
    let makes = useMakes(assetTypeMakes, distinctMakes);

    /** adding this use memo just to make sure this is carried out after auditAsset load  */
    const groupedMakes = useMemo(() => {
        const grpMakes = {};
        auditAssets.data &&
            auditAssets.data.forEach((item) => {
                const make = item.assetCore.make;
                if (!grpMakes[make]) grpMakes[make] = 1;
                else grpMakes[make] += 1;
            });
        return grpMakes;
    }, [auditAssets]);

    // build a list of makes that are within audit and sort them by the most popular
    const auditMakes = makes
        .filter((item) => groupedMakes[item.id])
        .sort((a, b) => groupedMakes[b.id] - groupedMakes[a.id])
        .map((item) => ({ makeGroup: "Assets from audit", ...item }));

    // remove makes that are in auditMakes
    makes = makes
        .filter((item) => !groupedMakes[item.id])
        .map((item) => ({ makeGroup: "All others", ...item }));

    const options = [...auditMakes, ...makes];
    const { cameraSource } = appSettings;

    useEffect(() => {
        if (distinctAssetTypes.length === 1) {
            setAltAsset({ ...altAssetData, type: distinctAssetTypes[0].id });
        }
    }, [distinctAssetTypes]);

    // populate available identifiers with a search value
    useEffect(() => {
        if (location.state && location.state.searchText) {
            const identName = searchedByIdentifier.name;
            if (Object.keys(identifiers).length > 0) {
                const values = Object.keys(identifiers).reduce(
                    (aggregate, key) => ({
                        ...aggregate,
                        [identName]: location.state.searchText,
                    }),
                    {}
                );
                const newAltAsset = { ...altAsset, ...values };
                setAltAsset(newAltAsset);
            }
        }
    }, [location, identifiers]);

    /** Clear errors from the state */
    const clearError = (name) => {
        const newErrors = { ...errors };
        delete newErrors[name];
        setErrors(newErrors);
    };

    const handleChange = (name: string) => (event: any) => {
        const { value } = event.target;
        setAltAsset({ ...altAsset, [name]: value });

        // on typing remove errors if any
        if (name === "identifier1") {
            if (value.length < identifier1MinLength) {
                const err = {
                    [name]: t("errors.identifiers1Length", {
                        identifier1MinLength,
                    }),
                };
                setErrors({ ...err, ...errors });
            } else {
                clearError(name);
            }
        } else if (errors[name]) {
            clearError(name);
        }
    };

    const clearMake = () => {
        setAltAsset({ ...altAsset, make: 0 });
    };

    const hdlIdentifierSelection = (value) => {
        try {
            setSearchedByIdentifier({ value, name: `identifier${value}` });

            if (value === 1)
                setAltAsset({
                    ...altAsset,
                    [`identifier${value}`]: location.state.searchText,
                    [`identifier${2}`]: "",
                });
            else if (value == 2)
                setAltAsset({
                    ...altAsset,
                    [`identifier${value}`]: location.state.searchText,
                    [`identifier${1}`]: "",
                });
        } catch (error) {
            const errStr = error.toString();
            logToSentry("Alt asset :hdlIdentifierSelection:", {
                error,
                errStr,
            });
            console.error(errStr);
        }
    };

    const onModalHide = () => setIsModalVisible(false);

    const onCameraError = (error) => {
        console.error(error);
        const link = (
            <CameraLocationError message={t("errors.noDeviceCamera")} />
        );
        toast.error(link);
        logToSentry("onCameraError:", {
            error,
        });
        onModalHide();
    };

    const handleModalShow = () => setIsModalVisible(true);

    const makeIdentifer = (name: string) => {
        const identifierName = identifiers[name];
        return (
            identifiers[name] && (
                <TextField
                    error={Boolean(errors[name])}
                    id={name}
                    label={t(name)}
                    value={altAsset[name]}
                    variant="outlined"
                    helperText={errors[name]}
                    placeholder={t("messages.placeHolderMessage", {
                        identifierName,
                    })}
                    onChange={handleChange(name)}
                    className={classes.margin}
                    fullWidth
                    inputProps={{
                        "data-testid": `alt-asset-${name}`,
                    }}
                />
            )
        );
    };

    const onCheckAsset = async () => {
        // check if identifiers are correct
        setLoading(true);
        const [hasValidationFailed, valErrors] = hdlIdentifierValidation(
            identifiers,
            altAsset,
            rules,
            typesList.invalidIdentifiers.data
        );

        if (hasValidationFailed) {
            setErrors({ ...errors, ...valErrors });
            setLoading(false);
            return;
        }

        if (!audit) {
            dispatch(actions.toast.create.error(t("errors.checkAltAsset")));
            setLoading(false);
            return;
        }

        try {
            const {
                identifier1,
                identifier2,
                identifier3,
                identifier4,
                make,
                note,
                type,
                auditImages,
            } = altAsset;

            const newUUID = uuid();

            const newAltAsset = {
                identifier1: identifier1.toUpperCase(),
                identifier2: identifier2.toUpperCase(),
                identifier3,
                identifier4,
                audit,
                createdBy: auth.account.id,
                createdDate: new Date().toISOString(),
                make,
                auditImages,
                assetSubmitted: false,
                notes: note.content
                    ? [
                          {
                              ...note,
                              createdDate: new Date().toISOString(),
                              id: uuid(),
                          },
                      ]
                    : [],
                type,
                // new asset does not have incremental db id, therefore
                // we temporarily generate it to avoid UI errors within reduce
                id: newUUID,
                uuid: newUUID,
            };

            if (!auditUpload[auditId].firstAssetChecked)
                await dispatch(
                    actions.auditUpload.create.update({
                        [auditId]: {
                            auditSignature: {
                                ...auditUpload[auditId].auditSignature,
                            },
                            firstAssetChecked: new Date().toISOString(),
                        },
                    })
                );

            await dispatch(
                actions.altAssets.create.writeRequest([newAltAsset])
            );
            /** If net is connected do a submission */
            if (netConnected && imageSyncEnabled)
                dispatch(
                    actions.auditUpload.create.partialSubmitRequest(
                        parseInt(auditId),
                        audits.item.firstAssetChecked
                    )
                );

            setLoading(false);
            navigate(`/auth/audits/${auditId}/assets/list`);
        } catch (error) {
            const errStr = error.toString();
            logToSentry("Alt asset :onCheckAsset:", {
                error,
                errStr,
            });
            setLoading(false);
        }
    };

    const onNoteChangeText = (e: object) => {
        try {
            const { value } = e.target;
            const next = { ...altAsset.note, content: value };
            setAltAsset({ ...altAsset, note: next });
        } catch (error) {
            const errStr = error.toString();
            logToSentry("Alt asset :onNoteChangeText:", {
                error,
                errStr,
            });
            console.error(errStr);
        }
    };

    const onPhotoChange = (photo: any) => {
        try {
            const next = altAsset.auditImages.map((item) =>
                item.id === photo.id ? photo : item
            );
            setAltAsset({ ...altAsset, auditImages: next });
        } catch (error) {
            const errStr = error.toString();
            logToSentry("Alt asset :onPhotoChange", {
                error,
                errStr,
            });
            console.error(errStr);
        }
    };

    const onPhotoDelete = (photo: any) => {
        try {
            const next = altAsset.auditImages.filter(
                ({ id }) => id !== photo.id
            );
            setAltAsset({ ...altAsset, auditImages: next });
        } catch (error) {
            const errStr = error.toString();
            logToSentry("Alt asset :onPhotoDelete", {
                error,
                errStr,
            });
            console.error(errStr);
        }
    };

    const onPhotoSuccess = (photo: any) => {
        try {
            const next = [photo, ...altAsset.auditImages];
            setAltAsset({ ...altAsset, auditImages: next });
            onModalHide();
        } catch (error) {
            const errStr = error.toString();
            logToSentry("Alt asset :onPhotoSuccess", {
                error,
                errStr,
            });
            console.error(errStr);
        }
    };

    const onValidate = () => {
        try {
            if (Object.keys(errors).length > 0) {
                return false;
            }
            const { identifier1, identifier2, identifier3, identifier4 } =
                altAsset;
            if (!(identifier1 || identifier2 || identifier3 || identifier4)) {
                return false;
            } else if (altAsset.auditImages.length === 0) {
                return false;
            } else if (!(altAsset.make && altAsset.type)) {
                return false;
            }
            return true;
        } catch (error) {
            const errStr = error.toString();
            logToSentry("Alt asset :onValidate", {
                error,
                errStr,
            });
            console.error(errStr);
        }
    };

    const triggerCamera = () => {
        if (cameraSource === "native") setImageTypeModalOpen(true);
        else handleModalShow();
    };

    // TODO: temporary to support NativeCamera
    const hdlItemSelection = (newImageType) => () => {
        try {
            setAltImageType(newImageType);
            setImageTypeModalOpen(false);
            document.getElementById("camera-file-upload-0").click();
        } catch (error) {
            const errStr = error.toString();
            logToSentry("Alt asset :hdlItemSelection", {
                error,
                errStr,
            });
            console.error(errStr);
        }
    };

    const hasPhotos = Boolean(altAsset && altAsset.auditImages.length);

    return (
        <>
            <Box
                id="alt-asset-form"
                component="form"
                noValidate
                autoComplete="off"
                className={classes.boxForm}
                style={{ height: isScrollingPresent ? "auto" : "100%" }}
            >
                <FormControl fullWidth className={classes.margin}>
                    <TextField
                        id="asset-type-select"
                        select
                        value={altAsset.type}
                        fullWidth
                        label={t("assetType")}
                        onChange={handleChange("type")}
                        data-testid={"asset-type-picker"}
                    >
                        <MenuItem value={0}>
                            <em>None</em>
                        </MenuItem>
                        {assetTypes.map((item, index) => (
                            <MenuItem
                                value={item.id}
                                key={index}
                                className={classes.capitalizeText}
                            >
                                {item.name}
                            </MenuItem>
                        ))}
                    </TextField>
                </FormControl>
                {Object.keys(identifiers).length > 0 && (
                    <Box className={classes.identifiersWrp}>
                        <FormControl fullWidth className={classes.margin}>
                            <FormLabel id="identifier-buttons-group-label">
                                {t("SearchedBy")}
                            </FormLabel>
                            <ButtonGroup
                                disableElevation
                                aria-label="Disabled elevation buttons"
                            >
                                <Button
                                    onClick={() => hdlIdentifierSelection(2)}
                                    variant={
                                        searchedByIdentifier.value === 2
                                            ? "contained"
                                            : "outlined"
                                    }
                                >
                                    {t("identifier2")}
                                </Button>
                                <Button
                                    onClick={() => hdlIdentifierSelection(1)}
                                    variant={
                                        searchedByIdentifier.value === 1
                                            ? "contained"
                                            : "outlined"
                                    }
                                >
                                    {t("identifier1")}
                                </Button>
                            </ButtonGroup>
                        </FormControl>
                        <FormControl fullWidth className={classes.margin}>
                            {makeIdentifer("identifier2")}
                            {makeIdentifer("identifier1")}
                            {makeIdentifer("identifier3")}
                            {makeIdentifer("identifier4")}
                        </FormControl>
                    </Box>
                )}
                <FormControl fullWidth className={classes.margin}>
                    <Autocomplete
                        id="makes-id"
                        options={options}
                        groupBy={(option) => option.makeGroup}
                        getOptionLabel={(option) => option.name}
                        fullWidth
                        data-testid={"asset-make-picker"}
                        PopperComponent={StyledPopper}
                        onInputChange={(e, newInputValue) => {
                            if (!newInputValue) clearMake();
                        }}
                        isOptionEqualToValue={(option, newValue) => {
                            return option.id === newValue.id;
                        }}
                        onChange={(e, newValue) => {
                            if (newValue) {
                                handleChange("make")({
                                    target: { value: newValue.id },
                                });
                            }
                        }}
                        renderInput={(params) => (
                            <TextField {...params} label={t("make_other")} />
                        )}
                        renderGroup={(params) => {
                            return (
                                <li
                                    key={params.key}
                                    className={classes.makesListBgr}
                                >
                                    <GroupHeader>{params.group}</GroupHeader>
                                    <GroupItems
                                        className={classes.capitalizeText}
                                    >
                                        {params.children}
                                    </GroupItems>
                                </li>
                            );
                        }}
                    />
                </FormControl>

                {hasPhotos && (
                    <PhotoList
                        assetTypeId={altAsset.type}
                        onChange={onPhotoChange}
                        onDelete={onPhotoDelete}
                        photos={altAsset.auditImages}
                        className={classes.margin}
                    />
                )}

                <TextField
                    id="alt-asset-note"
                    label={t("addANote")}
                    multiline
                    fullWidth
                    onChange={onNoteChangeText}
                    placeholder={t("addANote")}
                    rows={4}
                    value={altAsset.note.content}
                    className={classes.margin}
                    inputProps={{
                        "data-testid": "add-alt-asset-note",
                    }}
                />
            </Box>
            <ActionBar>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <Button
                            variant="outlined"
                            color="primary"
                            disabled={loading || !altAsset.type}
                            fullWidth
                            onClick={triggerCamera}
                            endIcon={<AddAPhotoIcon />}
                        >
                            {t(hasPhotos ? "addPhoto" : "takePhoto")}
                        </Button>
                    </Grid>
                    <Grid item xs={6}>
                        <LoadingButton
                            variant="contained"
                            color="primary"
                            disabled={!onValidate() || loading}
                            loading={loading}
                            fullWidth
                            loadingPosition="end"
                            onClick={onCheckAsset}
                            endIcon={<CheckIcon />}
                        >
                            {t("checkAsset")}
                        </LoadingButton>
                    </Grid>
                </Grid>
            </ActionBar>

            {/* TODO: Remove BrowserCamera in the future */}
            {cameraSource === "native" ? (
                <>
                    <NativeCamera
                        step={0}
                        imgIndex={0}
                        altImageType={altImageType}
                        onImageCapture={onPhotoSuccess}
                    />
                    <ImageTypeModal
                        open={imageTypeModalOpen}
                        setOpen={setImageTypeModalOpen}
                        assetTypeId={altAsset.type}
                        hdlItemSelection={hdlItemSelection}
                    />
                </>
            ) : (
                <BrowserCamera
                    assetTypeId={altAsset.type}
                    isVisible={isModalVisible}
                    photos={altAsset.auditImages}
                    onClose={onModalHide}
                    onSuccess={onPhotoSuccess}
                    onError={onCameraError}
                />
            )}
        </>
    );
};
